// IE Hub - Document Preview Dialog Component
// Shows uploaded documents (images/PDFs) in a modal dialog instead of new tab

import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
  CloseIcon,
} from "../../../ui";

export interface DocumentPreviewDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Document name for display */
  documentName?: string;
  /** Blob URL or file URL to preview */
  documentUrl?: string | null;
  /** Loading state while fetching document */
  isLoading?: boolean;
  /** Error message if document failed to load */
  error?: string | null;
  /** MIME type of the document (to determine preview type) */
  mimeType?: string;
}

const getFileTypeFromUrl = (url: string): "image" | "pdf" | "unknown" => {
  const lowerUrl = url.toLowerCase();
  if (
    lowerUrl.includes(".jpg") ||
    lowerUrl.includes(".jpeg") ||
    lowerUrl.includes(".png") ||
    lowerUrl.includes(".gif") ||
    lowerUrl.includes(".webp")
  ) {
    return "image";
  }
  if (lowerUrl.includes(".pdf")) {
    return "pdf";
  }
  return "unknown";
};

const getFileTypeFromMime = (mimeType: string): "image" | "pdf" | "unknown" => {
  if (mimeType.startsWith("image/")) {
    return "image";
  }
  if (mimeType === "application/pdf") {
    return "pdf";
  }
  return "unknown";
};

/**
 * DocumentPreviewDialog
 * Modal dialog for previewing uploaded documents (images and PDFs).
 * Shows loading state while document is being fetched.
 */
const DocumentPreviewDialog: React.FC<DocumentPreviewDialogProps> = ({
  open,
  onClose,
  documentName = "Document Preview",
  documentUrl,
  isLoading = false,
  error = null,
  mimeType,
}) => {
  const [imageError, setImageError] = useState(false);

  // Reset image error when URL changes
  useEffect(() => {
    setImageError(false);
  }, [documentUrl]);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  // Determine file type from MIME type or URL
  const fileType = mimeType
    ? getFileTypeFromMime(mimeType)
    : documentUrl
      ? getFileTypeFromUrl(documentUrl)
      : "unknown";

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box data-testid="components-uu-uu-ui-render-content-container-action-1"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 300,
            gap: 2,
          }}
        >
          <CircularProgress size={48} sx={{ color: "#3B82F6" }} />
          <Typography data-testid="components-uu-uu-ui-render-content-text-loading-document..." sx={{ color: "#6B7280", fontSize: "14px" }}>
            Loading document...
          </Typography>
        </Box>
      );
    }

    if (error || !documentUrl) {
      return (
        <Box data-testid="components-uu-uu-ui-render-content-container-action-2"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 300,
            gap: 2,
            p: 3,
          }}
        >
          <Typography data-testid="components-uu-uu-ui-render-content-text-action-1" sx={{ color: "#EF4444", fontSize: "14px", textAlign: "center" }}>
            {error || "Unable to load document. Please try again."}
          </Typography>
        </Box>
      );
    }

    if (imageError) {
      return (
        <Box data-testid="components-uu-uu-ui-render-content-container-action-3"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 300,
            gap: 2,
            p: 3,
          }}
        >
          <Typography data-testid="components-uu-uu-ui-render-content-text-action-2" sx={{ color: "#EF4444", fontSize: "14px", textAlign: "center" }}>
            Failed to display the document. The file may be corrupted or in an unsupported format.
          </Typography>
        </Box>
      );
    }

    // Render based on file type
    if (fileType === "image") {
      return (
        <Box data-testid="components-uu-uu-ui-render-content-container-action-4"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#F8FAFC",
            borderRadius: "8px",
            p: 1,
            minHeight: 200,
          }}
        >
          <Box data-testid="components-uu-uu-ui-render-content-container-action-5"
            component="img"
            src={documentUrl}
            alt={documentName}
            onError={handleImageError}
            sx={{
              maxWidth: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
              borderRadius: "4px",
            }}
          />
        </Box>
      );
    }

    if (fileType === "pdf") {
      return (
        <Box data-testid="components-uu-uu-ui-render-content-container-action-6"
          sx={{
            width: "100%",
            height: "70vh",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <Box data-testid="components-uu-uu-ui-render-content-container-action-7"
            component="iframe"
            src={documentUrl}
            title={documentName}
            sx={{
              width: "100%",
              height: "100%",
              border: "none",
            }}
          />
        </Box>
      );
    }

    // Unknown file type - try to display as image, with fallback message
    return (
      <Box data-testid="components-uu-uu-ui-render-content-container-action-8"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
          gap: 2,
          p: 3,
        }}
      >
        <Box data-testid="components-uu-uu-ui-render-content-container-action-9"
          component="img"
          src={documentUrl}
          alt={documentName}
          onError={handleImageError}
          sx={{
            maxWidth: "100%",
            maxHeight: "60vh",
            objectFit: "contain",
            borderRadius: "4px",
          }}
        />
      </Box>
    );
  };

  return (
    <Dialog data-testid="components-uu-uu-ui-document-preview-dialog-modal-action"
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Header */}
      <DialogTitle data-testid="components-uu-uu-ui-document-preview-dialog-header-action"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #E5E7EB",
          py: 1.5,
          px: 2,
        }}
      >
        <Typography data-testid="components-uu-uu-ui-document-preview-dialog-text-action"
          sx={{
            fontWeight: 600,
            fontSize: "16px",
            color: "#1F2937",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "calc(100% - 48px)",
          }}
        >
          {documentName}
        </Typography>
        <IconButton data-testid="components-uu-uu-ui-document-preview-dialog-btn-on-close"
          onClick={onClose}
          size="small"
          sx={{
            color: "#6B7280",
            "&:hover": {
              backgroundColor: "#F3F4F6",
              color: "#1F2937",
            },
          }}
          aria-label="Close preview"
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 2 }}>{renderContent()}</DialogContent>
    </Dialog>
  );
};

export default DocumentPreviewDialog;







